import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import AddGrowthHackModal from '../AddGrowthHackModal/AddGrowthHackModal';
import EditGrowthHackModal from '../EditGrowthHackModal/EditGrowthHackModal';
import BackdropLoader from '../Loader/BackdropLoader';
import { loadFromLocalStorage, saveToLocalStorage, removeFromLocalStorage } from '../../utils/localStorage';
import { useFilters } from '../../context/FilterContext.jsx';
import { insertGrowthHack, mapFormDataToApiPayload, fetchGrowthHacks, updateGrowthHack, fetchCredentials, fetchGrowthHackContacts } from '../../services/api/growthHacksService';
import './GrowthHacksTable.scss';
import TextField from "@mui/material/TextField";
import Pagination from "@mui/material/Pagination";
import ToastSnackbar from '../utilities/ToastSnackbar.jsx';

export default function GrowthHacksTable() {
  const navigate = useNavigate();
  const { filteredGrowthHacks, filters, filtersResetCount } = useFilters();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingGrowthHack, setAddingGrowthHack] = useState(false);
  const [updatingGrowthHack, setUpdatingGrowthHack] = useState(false);
  const [loadingRowClick, setLoadingRowClick] = useState(false);
  const [apiRawData, setApiRawData] = useState([]);
  const [totalGrowthHacks, setTotalGrowthHacks] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRowData, setEditRowData] = useState(null);

  const PAGE_STORAGE_KEY = 'growthHacksCurrentPage';
  const initialPageValue = useMemo(() => {
    const stored = loadFromLocalStorage(PAGE_STORAGE_KEY, 1);
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }, []);

  const [currentPage, setCurrentPageState] = useState(initialPageValue);
  const [goToPageInput, setGoToPageInput] = useState(String(initialPageValue));
  const rowsPerPage = 10;
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [canAddGrowthHack, setCanAddGrowthHack] = useState(false);
  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));

  const handleToastClose = () => {
    setToastOpen(false);
  };

  useEffect(() => {
    setGoToPageInput(String(currentPage));
    saveToLocalStorage(PAGE_STORAGE_KEY, currentPage);
  }, [currentPage]);

  useEffect(() => {
    try {
      const storedRoles = sessionStorage.getItem("userRoles");

      if (!storedRoles) {
        setCanAddGrowthHack(false);
        return;
      }

      const parsedRoles = JSON.parse(storedRoles);

      if (!Array.isArray(parsedRoles)) {
        setCanAddGrowthHack(false);
        return;
      }

      const allowedRoles = new Set(["accountleader", "solutionleader"]);
      const hasPermission = parsedRoles
        .map((role) =>
          String(role || "")
            .toLowerCase()
            .replace(/\s+/g, "")
        )
        .some((role) => allowedRoles.has(role));

      setCanAddGrowthHack(hasPermission);
    } catch (error) {
      console.error("Failed to parse user roles from session storage:", error);
      setCanAddGrowthHack(false);
    }
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (currentPage > totalPages) {
      setCurrentPageState(totalPages);
    }
  }, [currentPage, totalPages, loading]);

  useEffect(() => {
    const handleBeforeUnload = () => removeFromLocalStorage(PAGE_STORAGE_KEY);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const updatePage = useCallback((page) => {
    const parsed = Number(page);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setCurrentPageState(1);
      return;
    }
    const clamped = Math.min(Math.max(1, Math.floor(parsed)), totalPages);
    setCurrentPageState(clamped);
  }, [totalPages]);

  useEffect(() => {
    if (filtersResetCount > 0) {
      updatePage(1);
    }
  }, [filtersResetCount, updatePage]);

  const handleRowClick = async (event, id) => {
    const isEditButton = event.target.closest('.edit-btn');
    if (isEditButton) {
      return;
    }

    try {
      setLoadingRowClick(true);
      
      const clickedRow = allRows.find(row => row.id === id);
      console.log('Clicked row (mapped):', clickedRow);

      // Find the original API object for this row using GrowthHackPotentialClientID
      let originalGrowthHackObj = null;
      if (clickedRow?.GrowthHackPotentialClientID) {
        originalGrowthHackObj = apiRawData.find(
          (o) => o?.GrowthHackPotentialClientID === clickedRow.GrowthHackPotentialClientID
        ) || null;
      }
      if (!originalGrowthHackObj) {
        console.warn('Original GrowthHack object not found; falling back to mapped row.');
        originalGrowthHackObj = clickedRow;
      }

      if (clickedRow && clickedRow.GrowthHackPotentialClientID) {
        try {
          console.log('Fetching credentials for Growth Hack ID:', clickedRow.GrowthHackPotentialClientID);
          const credentialsResult = await fetchCredentials(clickedRow.GrowthHackPotentialClientID);
          const contactsResult = await fetchGrowthHackContacts(clickedRow.GrowthHackPotentialClientID);

          if (credentialsResult.success && contactsResult.success) {
            console.log('Credentials fetched successfully:', credentialsResult.data);
            navigate('/growthhack-tabledata', {
              state: {
                individualGHData: credentialsResult.data,
                growthHackContacts: contactsResult.data,
                growthHackInfo: originalGrowthHackObj
              }
            });
            return;
          } else {
            console.warn('Failed to fetch credentials:', credentialsResult.error);
          }

        } catch (error) {
          console.error('Error fetching credentials:', error);
        }
      } else {
        console.warn('No GrowthHackPotentialClientID found for the clicked row');
      }

      navigate('/growthhack-tabledata');
    } catch (error) {
      console.error('Error in handleRowClick:', error);
    } finally {
      setLoadingRowClick(false);
    }
  };

  const handleEdit = (event, row) => {
    event.stopPropagation();
    const isRowEditable = typeof row?.IsEditable === 'boolean'
      ? row.IsEditable
      : String(row?.IsEditable ?? row?.isEditable ?? '')
          .toLowerCase() === 'yes';

    if (!isRowEditable) {
      return;
    }
    setEditRowData(row || null);
    setEditModalOpen(true);
  };
  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditRowData(null);
  };

  const handleEditModalSubmit = async (formData) => {
    try {
      setUpdatingGrowthHack(true);
      const growthHackId = editRowData?.GrowthHackPotentialClientID;
      
      if (!growthHackId) {
        console.error("No GrowthHackPotentialClientID found for the selected row");
        alert("Error: Could not find the Growth Hack ID to update.");
        return;
      }

      const result = await updateGrowthHack(
        growthHackId,
        formData.status,
        formData.reason
      );

      if (result.success) {
        console.log("Growth hack successfully updated:", result.data);
        // Reload fresh data so the row reflects backend state immediately
        await loadGrowthHacksData();
        
        setEditModalOpen(false);
        setEditRowData(null);
        
        
      } else {
        console.error("Failed to update growth hack:", result.error);
        alert(`Failed to update growth hack: ${result.error}`);
      }
      setToastMessage(result.message);
      setToastType(result.success ? "success" : "error");
      setToastOpen(true);
    } catch (error) {
      console.error("Error updating growth hack:", error);
      alert("An unexpected error occurred while updating the growth hack.");
    } finally {
      setUpdatingGrowthHack(false);
    }
  };
  
  const filteredCount = Array.isArray(filteredGrowthHacks) ? filteredGrowthHacks.length : 0;
  useEffect(() => {
    console.log("filteredGrowthHacks length:", filteredCount);
  }, [filteredCount]);

  const formatApiDate = useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      return date.toLocaleDateString('en-GB', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Return original if formatting fails
    }
  }, []);

  const mapApiDataToTableFormat = useCallback((apiResponse) => {
    if (!apiResponse || !Array.isArray(apiResponse)) {
      console.log('API response is not an array or is null/undefined');
      return [];
    }
    return apiResponse.map((item, index) => ({
      id: item.id || (index + 1).toString(),
      Client: item.ClientName || '',
      Title: item.GrowthHackTitleGenAI || item.GrowthHackTitle || 'Under Evaluation ...',
      Tags: item.EYServiceGenAI || 'Under Evaluation ...',
      Score: item.GrowthHackScore || 0,
      Date: formatApiDate(item.Date) || '',
      GrowthHackPotentialClientID: item.GrowthHackPotentialClientID || '',
      Status: item.Status || '',
      IsEditable: String(item.isEditable ?? item.IsEditable ?? '')
        .toLowerCase() === 'yes'
    }));
  }, [formatApiDate]);

  const [allRows, setAllRows] = useState([]);
  
  const loadGrowthHacksData = useCallback(async (pageOverride) => {
    try {
      setLoading(true);
      const pageToUse = pageOverride ?? currentPage;
      console.log("Loading growth hacks data with fiscal year:", filters.fiscalYear, "sub sector:", filters.subSector, "account ID:", filters.accountId, "client ID:", filters.clientId, "GH sub-category:", filters.ghType, "GH type:", filters.category, "and GH priority:", filters.ghPriority, "page:", pageToUse);
  const APIresult = await fetchGrowthHacks("", filters.fiscalYear, filters.subSector, filters.accountId, filters.clientId, filters.ghType, filters.category, filters.ghPriority, pageToUse, rowsPerPage);
      
      const result = {
        success: true,
        data: APIresult
      };
      console.log("result API Service", result);
      
      if (result.data) {
        setApiRawData(result.data); // keep the original list to retrieve full objects later
  const mappedData = mapApiDataToTableFormat(result.data);
  setAllRows(mappedData);
        
        if (result.data.length > 0 && result.data[0].TotalGrowthHacks) {
          setTotalGrowthHacks(result.data[0].TotalGrowthHacks);
        } else {
          setTotalGrowthHacks(mappedData.length);
        }

        if (result.data.length > 0 && result.data[0].TotalRecords) {
          setTotalRecords(result.data[0].TotalRecords);
        } else {
          setTotalRecords(mappedData.length);
        }
      } else {
        console.warn("Failed to fetch growth hacks data, using empty data:", result.error);
        setAllRows([]);
        setTotalGrowthHacks(0);
        setTotalRecords(0);
        setToastMessage(result.data.message);
        setToastType("error");
        setToastOpen(true);
      }
    } catch (error) {
      console.error("Error loading growth hacks data:", error);
      setAllRows([]);
      setTotalGrowthHacks(0);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [filters.fiscalYear, filters.subSector, filters.accountId, filters.clientId, filters.ghType, filters.category, filters.ghPriority, currentPage, rowsPerPage, mapApiDataToTableFormat]);

  useEffect(() => {
    loadGrowthHacksData();
  }, [loadGrowthHacksData]);
  
  useEffect(() => {
    console.log("Saving to localStorage:", allRows);
    saveToLocalStorage('growthHacksData', allRows);
  }, [allRows]);
  
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitGrowthHack = async (formData) => {
    try {
      setAddingGrowthHack(true);
      const apiPayload = mapFormDataToApiPayload(formData);
      const result = await insertGrowthHack(apiPayload);
      
      if (result.success) {
        console.log("Growth hack successfully saved to backend:", result.data);
        updatePage(1);
        await loadGrowthHacksData(1);
        setIsModalOpen(false);
      } else {
        console.error("Failed to save growth hack to backend:", result.error);
        alert(`Failed to save growth hack: ${result.error}`);
      }
      setToastMessage(result.message);
      setToastType(result.success ? "success" : "error");
      setToastOpen(true);
    } catch (error) {
      console.error("Error submitting growth hack:", error);
      alert("An unexpected error occurred while saving the growth hack.");
    } finally {
      setAddingGrowthHack(false);
    }
  };

  // const formatTagsForTooltip = (tags) => {
  //   if (!tags) return '';
  //   const list = Array.isArray(tags)
  //     ? tags
  //     : String(tags)
  //         .split(',')
  //         .map(s => s.trim())
  //         .filter(Boolean);
  //   return list.map(t => (t.startsWith('#') ? t : `#${t}`)).join('\n');
  // };

  const renderTagsPills = (tags) => {
    if (!tags) return <Typography variant="body2" style={{ color: '#33333d' }}>-</Typography>;
    
    const tagsList = Array.isArray(tags)
      ? tags
      : String(tags)
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
    
    if (tagsList.length === 0) {
      return <Typography variant="body2" style={{ color: '#33333d' }}>-</Typography>;
    }
    
    const visibleTags = tagsList.slice(0, 3);
    const remainingCount = tagsList.length - 3;
    
    return (
      <Box className="tag-pills-container" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {visibleTags.map((tag, index) => (
          <Chip 
            key={index}
            label={tag}
            size="small"
            className="tag-pill"
          />
        ))}
        {remainingCount > 0 && (
          <Tooltip 
            title={
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Additional Tags:
                </Typography>
                {tagsList.slice(3).map((tag, index) => (
                  <Typography key={index} variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                    {tag}
                  </Typography>
                ))}
              </Box>
            }
            placement="top"
            arrow
          >
            <Chip 
              label={`+${remainingCount}`}
              size="small"
              className="tag-pill remaining-count"
            />
          </Tooltip>
        )}
      </Box>
    );
  };
  
  return (
    <div className='top-div-main'>
      <div className="button-wrapper">
      <Button 
          variant="contained" 
          color="primary" 
          className="add-btn"
          onClick={handleOpenModal}
          disabled={!canAddGrowthHack}
        >
          + Add Growth Hack
        </Button>
        </div>
    <Box className="growth-hacks-table">
      <Box className="table-header">
        <Typography variant="h6" style={{ color: '#ffffff', fontWeight: 600, paddingLeft: '10px' }}>
          {loading ? 'Loading...' : `${totalRecords} Growth Hacks are Available`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        
      </Box>
      <AddGrowthHackModal 
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitGrowthHack}
      />
      <EditGrowthHackModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        onSubmit={handleEditModalSubmit}
        initialStatus={editRowData?.Status || ''}
        initialReason={editRowData?.reason || ''}
        growthHackId={editRowData?.GrowthHackPotentialClientID || ''}
      />
        
      <TableContainer className='dashboard-table-main'>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="table-head-cell client-cell">Client</TableCell>
              <TableCell className="table-head-cell title-cell">Growth Hack Title</TableCell>
              <TableCell className="table-head-cell tags-cell">EY Service Tags</TableCell>
              <TableCell className="table-head-cell">GH Score</TableCell>
              <TableCell className="table-head-cell date-cell">Date</TableCell>
              <TableCell className="table-head-cell date-cell">Status</TableCell>
              <TableCell className="table-head-cell" align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGrowthHacks.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: '20px' }}>
                  <Typography variant="body1" style={{ color: '#7692acff' }}>
                    No growth hacks data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredGrowthHacks.map((row) => {
                const isRowEditable = typeof row.IsEditable === 'boolean'
                  ? row.IsEditable
                  : String(row.IsEditable ?? row.isEditable ?? '')
                      .toLowerCase() === 'yes';

                return (
                <TableRow 
                  key={row.id}
                  onClick={(e) => handleRowClick(e, row.id)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { 
                      backgroundColor: '#ffffff0a'
                    }
                  }}
                >
                   <TableCell className="client-cell">
                    <Tooltip 
                      title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{row.Client || '-'}</Typography>}
                      placement="top"
                      arrow
                    >
                      <span>{row.Client || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className="title-cell">
                    <Tooltip
                      title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{row.Title || '-'}</Typography>}
                      placement="top"
                      arrow
                      enterDelay={300}
                    >
                      <span className="clamped-title">{row.Title || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className="tags-cell">
                    {renderTagsPills(row.Tags)}
                  </TableCell>
                  <TableCell className="score-cell" align="center">
                    {row.Score || 0}
                  </TableCell>
                  <TableCell className="date-cell">
                    {row.Date || '-'}
                  </TableCell>
                  <TableCell className="date-cell">
                    {row.Status || '-'}
                  </TableCell>
                  <TableCell align="center">
                      {isRowEditable ? (
                        <Tooltip 
                          title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Edit Growth Hack</Typography>}
                          placement="top"
                          arrow
                        >
                          <span>
                            <IconButton
                              size="small"
                              className="edit-btn"
                              onClick={(e) => handleEdit(e, row)}
                              sx={{
                                color: '#FFD600',
                                background: 'transparent',
                                '&:hover': { background: 'rgba(255,255,255,0.08)' }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <span>
                          <IconButton
                            size="small"
                            className="edit-btn"
                            onClick={(e) => handleEdit(e, row)}
                            disabled
                            sx={{
                              color: '#5c5c63',
                              background: 'transparent',
                              cursor: 'not-allowed',
                              '&.Mui-disabled': {
                                color: '#5c5c63',
                                background: 'transparent',
                                cursor: 'not-allowed'
                              },
                              '&.Mui-disabled svg': {
                                color: '#5c5c63'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      )}
                  </TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
        
        {/* Pagination Container */}
        <Box className="pagination-wrapper">
          {/* Main Pagination - Centered */}
          <Box className="pagination-main">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => updatePage(value)}
              color="primary"
              size="medium"
            />
          </Box>

          {/* Go to Page Section - Positioned to the right */}
          <Box className="go-to-page-section">
            <Typography 
              variant="body2" 
              className="go-to-page-label"
            >
              Go to page:
            </Typography>
            <TextField
              type="number"
              size="small"
              value={goToPageInput}
              onChange={(e) => setGoToPageInput(e.target.value)}
              inputProps={{
                min: 1,
                max: totalPages,
              }}
              className="go-to-page-input"
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                updatePage(goToPageInput);
              }}
              className="go-to-page-button"
            >
              Go
            </Button>
          </Box>
        </Box>
      </TableContainer>
      
      {/* Backdrop Loader for blocking user interactions during table loading */}
      <BackdropLoader 
        open={loading} 
        message="Loading Growth Hacks..."
        color="primary"
        size={50}
      />
      
      {/* Backdrop Loader for adding growth hack */}
      <BackdropLoader 
        open={addingGrowthHack} 
        message="Adding Growth Hack..."
        color="primary"
        size={50}
      />
      
      {/* Backdrop Loader for updating growth hack */}
      <BackdropLoader 
        open={updatingGrowthHack} 
        message="Updating Growth Hack..."
        color="primary"
        size={50}
      />
      
      {/* Backdrop Loader for row click navigation */}
      <BackdropLoader 
        open={loadingRowClick} 
        message="Loading Growth Hack Details..."
        color="primary"
        size={50}
      />
      <ToastSnackbar
        open={toastOpen}
        message={toastMessage}
        onClose={handleToastClose}
        toastType={toastType}
      />
    </Box>
    </div>  
  );
}
